import { createSlice } from '@reduxjs/toolkit';
import { loadProjectExpenseSummary } from './thunks/loadProjectExpenseSummary.thunk';
import { loadProjectFormAttention } from './thunks/loadProjectFormAttention.thunk';
import { loadProjectWeather } from './thunks/loadProjectWeather.thunk';

/**
 * State of the redesigned project overview (the project dashboard's top card)
 * that outlives a visit:
 * - `expenseSummaries`: the expense card's computed summaries, by project,
 *   so revisiting a project within EXPENSE_SUMMARY_MAX_AGE_MS shows them at
 *   once. Each entry: `{ status: 'loading'|'succeeded'|'failed', summary,
 *   loadedAt, error }` — a reload keeps the previous summary on screen.
 * - `formAttention.nonConformance` / `formAttention.trMeters`: the
 *   Poikkeamaraportit and TR-mittarit lists, by project. Each entry:
 *   `{ status, data, noAccess, error }`, `data` the API payload (null with
 *   `noAccess`); a reload keeps the previous list on screen.
 * - `weather`: the current weather at each project's site. Each entry:
 *   `{ status, current, loadedAt, error }` (`current` null without a
 *   location); a reload keeps the previous reading on screen.
 */
const projectOverviewSlice = createSlice({
  name: 'projectOverview',
  initialState: {
    expenseSummaries: {},
    formAttention: {
      nonConformance: {},
      trMeters: {},
    },
    weather: {},
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadProjectExpenseSummary.pending, (state, action) => {
        const { projectId } = action.meta.arg;
        state.expenseSummaries[projectId] = {
          summary: null,
          loadedAt: null,
          ...state.expenseSummaries[projectId],
          status: 'loading',
          error: null,
        };
      })
      .addCase(loadProjectExpenseSummary.fulfilled, (state, action) => {
        const { projectId } = action.meta.arg;
        const { loadedAt, ...summary } = action.payload;
        state.expenseSummaries[projectId] = {
          status: 'succeeded',
          summary,
          loadedAt,
          error: null,
        };
      })
      .addCase(loadProjectExpenseSummary.rejected, (state, action) => {
        const { projectId } = action.meta.arg;
        state.expenseSummaries[projectId] = {
          summary: null,
          loadedAt: null,
          ...state.expenseSummaries[projectId],
          status: 'failed',
          error: action.error?.message || 'Failed to load the expense summary',
        };
      })
      .addCase(loadProjectFormAttention.pending, (state, action) => {
        const { projectId, kind } = action.meta.arg;
        const lists = (state.formAttention[kind] =
          state.formAttention[kind] || {});
        lists[projectId] = {
          data: null,
          noAccess: false,
          ...lists[projectId],
          status: 'loading',
          error: null,
        };
      })
      .addCase(loadProjectFormAttention.fulfilled, (state, action) => {
        const { projectId, kind } = action.meta.arg;
        const { noAccess, ...data } = action.payload || {};
        state.formAttention[kind][projectId] = {
          status: 'succeeded',
          data: noAccess ? null : data,
          noAccess: Boolean(noAccess),
          error: null,
        };
      })
      .addCase(loadProjectFormAttention.rejected, (state, action) => {
        const { projectId, kind } = action.meta.arg;
        const lists = state.formAttention[kind];
        lists[projectId] = {
          data: null,
          noAccess: false,
          ...lists[projectId],
          status: 'failed',
          error: action.error?.message || 'Failed to load the forms',
        };
      })
      .addCase(loadProjectWeather.pending, (state, action) => {
        const projectId = action.meta.arg.project.id;
        state.weather[projectId] = {
          current: null,
          loadedAt: null,
          ...state.weather[projectId],
          status: 'loading',
          error: null,
        };
      })
      .addCase(loadProjectWeather.fulfilled, (state, action) => {
        const projectId = action.meta.arg.project.id;
        state.weather[projectId] = {
          status: 'succeeded',
          current: action.payload.current,
          loadedAt: action.payload.loadedAt,
          error: null,
        };
      })
      .addCase(loadProjectWeather.rejected, (state, action) => {
        const projectId = action.meta.arg.project.id;
        state.weather[projectId] = {
          current: null,
          loadedAt: null,
          ...state.weather[projectId],
          status: 'failed',
          error: action.error?.message || 'Failed to load the weather',
        };
      });
  },
});

/** The expense card's entry of a project, or null before its first load. */
export const selectProjectExpenseSummaryEntry = (state, projectId) =>
  state.projectOverview?.expenseSummaries?.[projectId] || null;

/**
 * A project's Poikkeamaraportit (`kind` 'nonConformance') or TR-mittarit
 * ('trMeters') list entry, or null before its first load.
 */
export const selectProjectFormAttentionEntry = (state, kind, projectId) =>
  state.projectOverview?.formAttention?.[kind]?.[projectId] || null;

/** The current weather entry of a project's site, or null before its load. */
export const selectProjectWeatherEntry = (state, projectId) =>
  state.projectOverview?.weather?.[projectId] || null;

export default projectOverviewSlice.reducer;
